import Toast from "react-native-toast-message";
import React, { useContext, useEffect, useState } from "react";
import {
  Linking,
  Platform,
  RefreshControl,
  ScrollView,
  StyleSheet,
} from "react-native";
import { View, Text } from "../../components/Themed";
import Colors from "../../constants/Colors";
import OpenHouseList from "../../components/OpenHouseList";
import { OpenHouseListShimmer } from "../../components/OpenHouseListShimmer";
import StatSection from "../../components/StatSection";
import Button from "../../components/Themed/Button";
import PageHeader from "../../components/PageHeader";
import CreateEditOpenHouse from "../../modals/CreateEditOpenHouse";
import { OpenHouse } from "../../types";
import { OpenHouseAddMutation } from "../../data/openHouseMutation";
import Icon from "../../components/Icon";
import { useGetDashboardStats } from "../../hooks/useGetDashboardStats";
import GlobalContext from "../../context/Global";
import useNetworkHealth from "../../hooks/useNetworkHealth";
import { getLocalData } from "../../services/storage";
import { useFocusEffect } from "@react-navigation/native";
import Popup from "../../components/Popup";
import moment from "moment";
import * as Updates from "expo-updates";
import { OneSignal } from "react-native-onesignal";
import { useGetOpenHouses } from "../../hooks/useGetOpenHouses";
import useSyncOpenHouses from "../../hooks/useSyncOpenHouses";
import Pressable from "../../components/Pressable";
import ManualSyncButton from "../../components/ManualSyncButton";
import useLocation from "../../hooks/useLocation";

export const Home = ({ navigation }: any) => {
  const network = useNetworkHealth();
  const [showOpenHouseModal, setShowOpenHouseModal] = useState<boolean>(false);
  const [sync, setSync] = useState<boolean>(false);
  const [logoutWarning, setLogoutWarning] = React.useState(false);
  const [daysTilLogout, setDaysTilLogout] = React.useState(5);
  const { user, isTablet } = useContext(GlobalContext);
  const { requestPermissions } = useLocation();

  useEffect(() => {
    const subscription = Linking.addEventListener("url", ({ url }) => {
      url && handleDeepLink(url);
    });

    Linking.getInitialURL()
      .then((url) => {
        url && handleDeepLink(url);
      })
      .catch(console.warn);

    return () => {
      if (subscription && typeof subscription.remove === "function") {
        subscription.remove();
      }
    };
  }, []);

  const handleDeepLink = (url: string) => {
    const blossorId = url
      ?.split("/")
      ?.find((segment) => segment.startsWith("bid-"))
      ?.split("-")
      ?.slice(1, 3)
      ?.join("-");

    if (blossorId) {
      navigation.navigate("CreateOpenHouse", {
        blossorId: blossorId,
      });
    }
  };

  const {
    data: stats,
    isFetching: fetchingStats,
    isLoading: loadingStats,
    isFetched: fetchedStats,
    error,
    refetch: refetchStats,
  } = useGetDashboardStats(user?.openHouseUuid);

  const syncAllOpenHouses = React.useRef<any>(null);

  const syncOpenHouses = useSyncOpenHouses();

  const {
    data: activeOpenHouses,
    isFetching: fetchingActiveOpenHouses,
    isLoading: loadingActiveOpenHouses,
    error: activeOpenHousesError,
  } = useGetOpenHouses("active", user?.openHouseUuid);

  const {
    data: upcomingOpenHouses,
    isFetching: fetchingUpcomingOpenHouses,
    isLoading: loadingUpcomingOpenHouses,
    error: upcomingOpenHousesError,
  } = useGetOpenHouses("upcoming", user?.openHouseUuid);

  const {
    data: completedOpenHouses,
    isFetching: fetchingCompletedOpenHouses,
    isLoading: loadingCompletedOpenHouses,
    error: completedOpenHousesError,
  } = useGetOpenHouses("completed", user?.openHouseUuid);

  const {
    data: cachedOpenHouses,
    isFetching: fetchingCachedOpenHouses,
    error: cachedOpenHousesError,
  } = useGetOpenHouses("cached", user?.openHouseUuid);

  const totalOpenHouses =
    activeOpenHouses?.pages[0]?.totalCount +
    upcomingOpenHouses?.pages[0]?.totalCount +
    completedOpenHouses?.pages[0]?.totalCount;

  const isLoadingInitialData =
    loadingActiveOpenHouses ||
    loadingUpcomingOpenHouses ||
    loadingCompletedOpenHouses;

  React.useEffect(() => {
    codePushCheck();
  }, []);

  React.useEffect(() => {
    if (user && Platform.OS !== "web") {
      OneSignal.User.addTags({
        brivity_person_uuid: user?.person?.uuid,
        email: user?.email,
      });
      refetchStats();
    }
  }, [user]);

  // Request location permission when Home screen loads
  React.useEffect(() => {
    if (user) {
      // Request location permission (non-blocking, user can deny)
      requestPermissions().catch((error) => {
        console.log("Location permission request failed:", error);
      });
    }
  }, [user, requestPermissions]);

  const codePushCheck = async () => {
    if (!__DEV__ && Platform.OS !== "web") {
      try {
        const update = await Updates.checkForUpdateAsync();
        if (update.isAvailable) {
          await Updates.fetchUpdateAsync();
          Updates.reloadAsync();
        }
      } catch (e) {
        console.log(e);
      }
    }
  };

  const handleSync = async () => {
    try {
      const result = await syncOpenHouses();
      if (result.failed > 0) Toast.show({ type: "error", text1: "Some changes could not sync", text2: "Your pending changes are saved on this device. Tap Sync to retry." });
    } catch {
      Toast.show({ type: "error", text1: "Unable to sync", text2: "Your pending changes are saved on this device. Tap Sync to retry." });
    }
    refetchStats();
    getLocalCompleted();
  };

  useFocusEffect(
    React.useCallback(() => {
      if (network.latencyMs ?? null) {
        handleSync();
      }
      getLocalCompleted();
    }, [network.latencyMs ?? null])
  );

  const getLocalCompleted = async () => {
    let syncData = await getLocalData(`syncData`);
    if (syncData && syncData.length > 0) {
      setSync(true);
    } else {
      setSync(false);
    }
  };

  const createOpenHousePressed = () => {
    setShowOpenHouseModal(true);
  };

  const createOpenHouse = (addOpenHouse: any, data: OpenHouse) => {
    data?.tags?.unshift(data.address);
    data?.tags?.unshift("Open House");
    data?.tags?.unshift("openhouseapp");

    addOpenHouse.mutateAsync(data).then((data: any) => {
      setShowOpenHouseModal(false);
      navigation.navigate("OpenHomeDetails", { openHouseId: data.id });
    });
  };

  const PageTitle = (props: any) => (
    <Text
      size={20}
      lineHeight={28}
      weight="SemiBold"
      lightColor={Colors.nuetral_grey_700}
    >
      {props.children}
    </Text>
  );

  const HeaderIconContainer = (props: any) => (
    <View style={styles.HeaderIconContainer}>{props.children}</View>
  );

  const FormBuilderIcon = () => (
    <Icon
      name="folder-plus"
      size={24}
      color={Colors.primary_500}
      onPress={() => navigation.navigate("FormBuilder")}
    />
  );

  const AccountInformationIcon = () => (
    <Icon
      name="user"
      size={24}
      color={Colors.primary_500}
      testingId="account_information"
      onPress={() => navigation.navigate("AccountInformation")}
    />
  );

  const CreateOpenHouseButton = ({ isInHeader }: { isInHeader?: boolean }) => (
    <Button
      style={[
        Platform.OS === "web"
          ? styles.createOpenHouseButtonWeb
          : styles.createOpenHouseButton,
        isInHeader && styles.noMargin,
      ]}
      text="Create Open House"
      textColor={Colors.base_colors_white}
      lightColor={Colors.primary_500}
      darkColor={Colors.primary_500}
      disabled={network.isOffline ? true : false}
      onPress={() => {
        navigation.navigate("CreateOpenHouse");
      }}
    />
  );

  const GetStartedText = (props: any) => (
    <Text
      style={styles.getStartedText}
      size={16}
      lightColor={Colors.nuetral_grey_600}
    >
      {props.children}
    </Text>
  );

  const getTimeStamp = async () => {
    const timestamp = await getLocalData("@timestamp30Days");
    if (timestamp) {
      const daysDifference = moment(timestamp).diff(moment(), "days");
      if (daysDifference <= 5) {
        setDaysTilLogout(daysDifference);
        setLogoutWarning(true);
      }
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      sync && !network.isOffline && getTimeStamp();
    }, [network.latencyMs ?? null, sync])
  );

  return (
    <OpenHouseAddMutation>
      {(addOpenHouse) => (
        <>
          <View
            lightColor={Colors.background}
            darkColor={Colors.nuetral_grey_600}
            style={styles.container}
          >
            <PageHeader
              leftComponent={<PageTitle>Open Houses</PageTitle>}
              rightComponent={
                <HeaderIconContainer>
                  {Platform.OS === "web" && (
                    <CreateOpenHouseButton isInHeader={true} />
                  )}
                  <AccountInformationIcon />
                </HeaderIconContainer>
              }
              offlineBannerMessage="Some features may not display while offline."
            />
            <ScrollView
              showsVerticalScrollIndicator={false}
              style={[
                styles.scroll,
                Platform.OS === "web"
                  ? {
                      marginTop: isTablet ? 0 : -16,
                      width: "75%",
                      alignSelf: "center",
                    }
                  : {
                      marginTop: isTablet ? 0 : -16,
                    },
              ]}
              contentContainerStyle={styles.containerStyle}
              refreshControl={
                <RefreshControl
                  refreshing={fetchedStats ? loadingStats : false}
                  onRefresh={() => refetchStats()}
                />
              }
            >
              {Platform.OS !== "web" && (
                <CreateOpenHouseButton isInHeader={true} />
              )}
              <ManualSyncButton
                activeUuids={[activeOpenHouses, upcomingOpenHouses, completedOpenHouses].flatMap((houses: any) => houses?.pages?.flatMap((page: any) => page.openHouses?.map((house: any) => house.uuid) || []) || [])}
                onSynced={getLocalCompleted}
              />
              <StatSection />
              {isLoadingInitialData ? (
                <>
                  <OpenHouseListShimmer status="active" />
                  <OpenHouseListShimmer status="upcoming" />
                  <OpenHouseListShimmer status="completed" />
                </>
              ) : totalOpenHouses === 0 ? (
                <View
                  style={[
                    styles.getStartedContainer,
                    {
                      paddingHorizontal: isTablet ? 32 : 16,
                    },
                  ]}
                >
                  <GetStartedText>
                    It's easy to get started - just click the "Create Open
                    House" button to add your upcoming events to this screen.
                  </GetStartedText>
                </View>
              ) : (
                <>
                  <OpenHouseList
                    openHousesData={activeOpenHouses}
                    status="active"
                  />
                  <OpenHouseList
                    openHousesData={upcomingOpenHouses}
                    status="upcoming"
                  />
                  {sync && (
                    <OpenHouseList
                      openHousesData={cachedOpenHouses}
                      status="cached"
                      getLocalCompleted={getLocalCompleted}
                      ref={syncAllOpenHouses}
                    />
                  )}
                  <OpenHouseList
                    openHousesData={completedOpenHouses}
                    status="completed"
                  />
                </>
              )}
            </ScrollView>
          </View>
          <CreateEditOpenHouse
            show={showOpenHouseModal}
            onSubmit={(data) => createOpenHouse(addOpenHouse, data)}
            onClosed={() => setShowOpenHouseModal(false)}
          />
          {logoutWarning && (
            <Popup
              show={logoutWarning}
              header={
                <Text
                  size={16}
                  weight="Bold"
                  lightColor={Colors.nuetral_grey_700}
                >
                  Forced Sign In
                </Text>
              }
              body={
                <View style={styles.message}>
                  <Text
                    size={16}
                    lineHeight={24}
                    lightColor={Colors.nuetral_grey_700}
                  >
                    {`You'll be required to sign in again in ${daysTilLogout} day${
                      daysTilLogout > 1 ? "s" : ""
                    }. You could lose all unsynced data and leads if not synced in time. Ensure you sync your data before the deadline to avoid any data loss.`}
                  </Text>
                </View>
              }
              footer={
                <View
                  lightColor={Colors.base_colors_white}
                  darkColor={Colors.nuetral_grey_700}
                  style={styles.footerContainer}
                >
                  <Pressable
                    style={styles.cancelButton}
                    onPress={() => setLogoutWarning(false)}
                  >
                    <Text lightColor={Colors.nuetral_grey_700} weight="Bold">
                      Cancel
                    </Text>
                  </Pressable>
                  <Pressable
                    style={styles.button}
                    onPress={async () => {
                      await syncAllOpenHouses?.current?.syncAllOpenHouses();
                      setLogoutWarning(false);
                    }}
                  >
                    <Text lightColor={Colors.primary_500} weight="Bold">
                      Sync
                    </Text>
                  </Pressable>
                </View>
              }
            />
          )}
        </>
      )}
    </OpenHouseAddMutation>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 24,
  },
  HeaderIconContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 32,
  },
  createOpenHouseButton: {
    width: 186,
    alignSelf: "center",
    alignItems: "center",
    marginTop: 8,
  },
  createOpenHouseButtonWeb: {
    width: 186,
    alignSelf: "center",
    alignItems: "center",
    marginTop: 24,
    marginBottom: 16,
  },
  noMargin: {
    marginTop: 0,
    marginBottom: 0,
  },
  getStartedContainer: {
    backgroundColor: "transparent",
    height: 130,
    justifyContent: "center",
    alignItems: "center",
  },
  getStartedText: {
    textAlign: "center",
    width: "70%",
  },
  containerStyle: {
    paddingBottom: 100,
  },
  scroll: { flex: 1 },
  footerContainer: {
    height: 55,
    flexDirection: "row",
    borderTopColor: Colors.tinted_grey_300,
    borderTopWidth: 1,
    paddingVertical: 8,
  },
  button: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    borderLeftColor: Colors.tinted_grey_300,
    borderLeftWidth: 1,
  },
  cancelButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  message: { gap: 8 },
});
